import { useEffect, useMemo, useRef, useState } from 'react';
import type { IDuplicateProjectRow } from 'models';

interface SearchableSelectProps {
  /** Combined option list (pinned-first; hideOption already applied). */
  list: IDuplicateProjectRow[];
  /** Button label; '----' when empty (template `{{#if name}}`). */
  name: string;
  selectedId: number | null;
  /** Wrapper element id (the REAL typo id `duclicate-project-select`). */
  id?: string;
  /**
   * ★ 4e — the view's `none` option (`searchable_select.js:21`,
   * `searchable_select.tmpl:16`): a leading `<li data-value="" class="none-li">`
   * labelled `----`, which `select` (:212-232) resolves to
   * `select_item(null, '----')` because `$(li).data('value') || null` turns the
   * empty string into `null`. The sizer selection popup constructs its
   * SearchableSelect with `{none: true, w_100: true, id: 'project_select'}`
   * (`header/view/sizer_popup.js:121-128`); the Duplicate popup passes no `none`,
   * so the default keeps that call site byte-identical.
   */
  none?: boolean;
  /** Debounced (500ms) server search with the trimmed lowercased query. */
  onSearch: (value: string) => void;
  /** Infinite scroll: fires when fewer than 250px remain below. */
  onScrollEnd: () => void;
  onSelect: (id: number | null, name: string | null) => void;
}

const SEARCH_DEBOUNCE_MS = 500;
const SCROLL_THRESHOLD_PX = 250;
const OPTION_ROW_PX = 23;

/**
 * Searchable dropdown (`/static/mvc/view/searchable_select.js` +
 * searchable_select.tmpl) — the Duplicate popup's Target Project control
 * (B1.6 / parity 147). Faithful behaviors: button toggles
 * `.opened`/`.closed` (CSS `.selectdropbtn.opened + #searchableDropdown`
 * shows the panel); upward flip when `spaceAbove - 350 > spaceBelow`;
 * search input with 500ms debounce + `X` reset; infinite scroll within
 * 250px of the bottom; ↑/↓ hover navigation (scrollTop = hovered × 23) and
 * Enter selecting the hovered (or first) option; outside click closes.
 */
const SearchableSelect = ({
  list,
  name,
  selectedId,
  id,
  none = false,
  onSearch,
  onScrollEnd,
  onSelect,
}: SearchableSelectProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [opened, setOpened] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [hovered, setHovered] = useState(-1);

  const buttonLabel = useMemo(() => {
    if (name) return name;
    if (selectedId != null) {
      const found = list.find((row) => row.id === selectedId)?.name;
      if (found) return found;
    }
    return '----';
  }, [name, selectedId, list]);

  // Outside click closes (the body-level rand-class click filter).
  useEffect(() => {
    if (!opened) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // ↑/↓/Enter while open (body keydown in the legacy).
  useEffect(() => {
    if (!opened) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode !== 38 && e.keyCode !== 40 && e.keyCode !== 13) return;
      if (e.keyCode === 13) {
        const index = hovered > -1 ? hovered : 0;
        const row = list[index];
        if (row) selectRow(row);
        return;
      }
      setHovered((prev) => {
        let next = prev;
        if (e.keyCode === 40) next = prev === list.length - 1 ? 0 : prev + 1;
        else if (prev) next = prev - 1;
        if (listRef.current) listRef.current.scrollTop = next * OPTION_ROW_PX;
        return next;
      });
    };
    document.body.addEventListener('keydown', onKeyDown);
    return () => document.body.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, hovered, list]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const open = () => {
    setOpened(true);
    // Flip upward when there is much more space above (show()).
    const root = rootRef.current;
    const dropdown = dropdownRef.current;
    if (root && dropdown) {
      const rect = root.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceAbove - 350 > spaceBelow) dropdown.classList.add('top');
      else dropdown.classList.remove('top');
    }
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const close = () => {
    setOpened(false);
    setHovered(-1);
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  const selectRow = (row: IDuplicateProjectRow) => {
    close();
    setSearchValue('');
    onSelect(row.id ?? null, row.name ?? null);
  };

  const onFilterChange = (value: string) => {
    setSearchValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearch(value.trim().toLowerCase());
      setHovered(-1);
      if (listRef.current) listRef.current.scrollTop = 0;
    }, SEARCH_DEBOUNCE_MS);
  };

  const onScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    const bottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (bottom < SCROLL_THRESHOLD_PX) onScrollEnd();
  };

  return (
    <div className="searchable-select-dropdown" id={id} ref={rootRef}>
      <button
        type="button"
        className={`btn-click selectdropbtn ${opened ? 'opened' : 'closed'} w-100`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (opened) close();
          else open();
        }}
      >
        <span className="btn-click btn-name">{buttonLabel}</span>
        <span className="btn-click icon-down">&#9660;</span>
      </button>
      <div id="searchableDropdown" className="dropdown-content w-100" ref={dropdownRef}>
        <input
          ref={inputRef}
          type="search"
          placeholder="Search.."
          className="searchableInput"
          value={searchValue}
          onChange={(e) => onFilterChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const index = hovered > -1 ? hovered : 0;
              const row = list[index];
              if (row) selectRow(row);
            }
          }}
        />
        <span
          className={`reset-input${searchValue ? '' : ' hide'}`}
          onClick={() => {
            setSearchValue('');
            onSearch('');
          }}
        >
          X
        </span>
        <ul className="searchableDropdown-list" ref={listRef} onScroll={onScroll}>
          {none && (
            // searchable_select.tmpl:16 — `data-value=""` → `select_item(null, '----')`.
            <li
              data-value=""
              className="none-li"
              onClick={() => {
                close();
                setSearchValue('');
                onSelect(null, '----');
              }}
            >
              ----
            </li>
          )}
          {list.map((row, index) => (
            <li
              key={`${row.id}_${index}`}
              data-index={index}
              data-value={row.id}
              className={`${row.id === selectedId ? `selected ${row.name}` : ''}${
                index === hovered ? ' hovered' : ''
              }`}
              onClick={() => selectRow(row)}
            >
              {row.name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SearchableSelect;
